import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, ExternalLink, Sparkles } from "lucide-react";
import api from "@/lib/api";

export const yearLabel = (y) => (y == null ? "" : y < 0 ? `${Math.abs(y)} BC` : String(y));

const WHY = { "first-ever": "First ever", weird: "Weird", "oddly-true": "Oddly true" };

export const ReelCard = ({ it, testid, hideYear = false, onReveal }) => {
  const url = it.url || it.page?.url;
  const image = it.image || it.page?.thumbnail;
  return (
    <li data-testid={testid} className="flex flex-col bg-[#fbf8f1] border border-ink/15 rounded-sm shadow-[0_1px_0_rgba(17,24,39,0.08)] overflow-hidden">
      {image && (
        <a href={url} target="_blank" rel="noopener noreferrer" className="block">
          <img src={image} alt={it.page?.title || ""} loading="lazy" className="w-full h-36 object-cover grayscale-[30%] sepia-[15%] contrast-105" />
        </a>
      )}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2">
          <span data-testid={`${testid}-year`} className="font-vintage text-[28px] font-bold text-ink leading-none tracking-tight">{hideYear ? "????" : yearLabel(it.year)}</span>
          {hideYear && <button type="button" data-testid={`${testid}-reveal`} onClick={onReveal} className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold hover:underline text-right">Year hidden — guess below or reveal</button>}
          {WHY[it.why] && <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-ink text-ink text-[10px] font-bold uppercase tracking-[0.15em]"><Sparkles className="w-3 h-3" /> {WHY[it.why]}</span>}
        </div>
        <p className="mt-2 text-[14px] text-ink leading-relaxed flex-1">{it.text}</p>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-link`} className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-gold hover:underline">
            Read more → <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <p className="mt-1 text-[10px] uppercase tracking-wider text-ink-muted">Source: Wikipedia · CC BY-SA 4.0</p>
      </div>
    </li>
  );
};

const LABEL = { guardian: "The Guardian", nyt: "The New York Times" };
const READ = { guardian: "Read on The Guardian →", nyt: "Read on NYTimes.com →" };

const ArchiveCard = ({ h, testid }) => (
  <li data-testid={testid} data-source={h.source} className="flex gap-4 bg-[#fbf8f1] border border-ink/15 rounded-sm p-4">
    {h.image ? (
      <a href={h.url} target="_blank" rel="noopener noreferrer" className="shrink-0"><img src={h.image} alt="" loading="lazy" className="w-28 h-28 sm:w-36 sm:h-28 object-cover border border-ink/10" /></a>
    ) : (
      <div className="shrink-0 w-28 h-28 bg-ink/5 border border-ink/10 flex items-center justify-center"><Newspaper className="w-6 h-6 text-gold" /></div>
    )}
    <div className="min-w-0 flex-1 flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <span className="font-vintage text-[24px] font-bold text-ink leading-none">{h.year}</span>
        <span className="px-2 py-0.5 border border-ink text-ink text-[10px] font-bold uppercase tracking-[0.15em]">{LABEL[h.source] || h.source_label}{h.front_page ? " · Front page" : ""}</span>
      </div>
      <a href={h.url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-headline`} className="mt-2 text-[15px] font-semibold text-ink leading-snug hover:text-gold">{h.headline}</a>
      {h.trail && <p className="mt-1 text-[13px] text-ink-muted leading-relaxed flex-1">{h.trail.length > 120 ? `${h.trail.slice(0, 117).trimEnd()}…` : h.trail}</p>}
      <a href={h.url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-link`} className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-gold hover:underline">{READ[h.source] || "Read more →"} <ExternalLink className="w-3 h-3" /></a>
    </div>
  </li>
);

export const Archives = ({ day, testid }) => {
  const guardian = (day.guardian || []).slice(0, 2);
  const nyt = (day.nyt || []).slice(0, 1);
  if (guardian.length + nyt.length === 0) return null;
  return (
    <div data-testid={testid} className="mt-6 border-t-2 border-ink pt-3">
      <p className="font-vintage text-[15px] font-bold uppercase tracking-[0.12em] text-ink">From The {guardian.length ? "Guardian" : "New York Times"} Archives — On This Day</p>
      <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        {guardian.map((h, i) => <ArchiveCard key={`${h.url}-${i}`} h={h} testid={`${testid}-guardian-${i + 1}`} />)}
        {nyt.map((h, i) => <ArchiveCard key={`${h.url}-${i}`} h={h} testid={`${testid}-nyt-${i + 1}`} />)}
      </ul>
      <p className="mt-2 text-[11px] text-ink-muted">{[guardian.length && "Headlines via The Guardian Open Platform", nyt.length && "Historic front pages via the NYT Archive API"].filter(Boolean).join(" · ")}</p>
    </div>
  );
};

export const Attribution = ({ day }) => (
  <p data-testid="onthisday-attribution" className="mt-5 text-[11px] text-ink-muted border-t border-ink/10 pt-3">
    Facts from <a href="https://en.wikipedia.org/wiki/Wikipedia:On_this_day" target="_blank" rel="noopener noreferrer" className="underline hover:text-ink">Wikipedia On This Day API</a> (<a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener noreferrer" className="underline hover:text-ink">CC BY-SA 4.0</a>) via Wikimedia. Thumbnails CC BY-SA. Headlines via <a href="https://open-platform.theguardian.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-ink">The Guardian Open Platform</a>{day?.nyt?.length ? " and the NYT Archive API" : ""}. Historic pages via Library of Congress Chronicling America. Curated by OddlyTrue.{day?.provider === "muffinlabs" ? " Today's facts via MuffinLabs History API." : ""}
  </p>
);

export const StaleBanner = ({ day }) => day?.stale ? (
  <p data-testid="onthisday-stale" className="mb-3 px-3 py-2 bg-gold/15 border border-gold text-[12px] text-ink">Showing yesterday's oddities while we fetch today's.</p>
) : null;

export const OnThisDayReel = ({ ddmm }) => {
  const [day, setDay] = useState(null);
  const [failed, setFailed] = useState(false);
  const mmdd = ddmm ? `${ddmm.slice(3, 5)}-${ddmm.slice(0, 2)}` : null;
  useEffect(() => {
    setDay(null); setFailed(false);
    api.get("/onthisday", { params: mmdd ? { date: mmdd } : {} }).then((r) => setDay(r.data)).catch(() => setFailed(true));
  }, [mmdd]);
  if (failed) return null;
  return (
    <section id="what-happened" data-testid="onthisday-reel" className="mt-8 border-t-4 border-double border-ink pt-4">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Newspaper className="w-4 h-4 text-gold" />
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">What happened on this day · Odd history for today</p>
          </div>
          <h2 data-testid="onthisday-title" className="mt-1 font-vintage text-3xl md:text-4xl font-bold text-ink tracking-tight">Vintage News{day ? ` — ${day.label}` : ""}</h2>
        </div>
        {day && <Link to={`/history/${day.slug}`} data-testid="onthisday-more-link" className="text-[13px] font-semibold text-gold hover:underline">Full edition: {day.all_picks_count} odd things →</Link>}
      </div>
      <StaleBanner day={day} />
      {!day ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 bg-[#fbf8f1] border border-ink/10 animate-pulse" />)}</div>
      ) : (
        <>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="onthisday-picks">
            {day.picks.map((it, i) => <ReelCard key={`${it.year}-${i}`} it={it} testid={`onthisday-pick-${i + 1}`} />)}
          </ul>
          <Archives day={day} testid="onthisday-archives" />
          <Attribution day={day} />
        </>
      )}
    </section>
  );
};
